import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { searchProducts, getShopifyImageUrl, formatPrice, type SearchResult } from "@/lib/shopify";
import { listingPrice } from "@/lib/variants";
import { cleanProductTitle } from "@/lib/productTitle";

const STOP = new Set(["x", "and", "with", "of", "set", "the", "a", "in", "for", "by"]);

/** "48-black-aluminum-outdoor-coffee-table" -> "black aluminum outdoor coffee table". */
function handleToQuery(handle: string): string {
  return handle
    .split("-")
    .filter((t) => t && !/^\d+$/.test(t) && !STOP.has(t))
    .slice(0, 7)
    .join(" ");
}

/**
 * On a discontinued product's page: in-stock pieces that match the words of its
 * old URL. The supplier deletes products daily, and old pins and search results
 * keep sending people to them; a bare "no longer available" page loses them.
 */
export const DiscontinuedAlternatives = ({ handle }: { handle: string }) => {
  const query = handleToQuery(handle);
  const [results, setResults] = useState<SearchResult[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!query) {
      setResults([]);
      return;
    }
    searchProducts(query, 12)
      .then((r) => {
        if (cancelled) return;
        // The supplier lists some pieces several times under one title; show each once.
        const seen = new Set<string>();
        const unique = r.filter((p) => {
          const name = cleanProductTitle(p.title, p.productType).toLowerCase();
          if (p.availableForSale === false || p.handle === handle || seen.has(name)) return false;
          seen.add(name);
          return true;
        });
        setResults(unique.slice(0, 8));
      })
      .catch(() => !cancelled && setResults([]));
    return () => {
      cancelled = true;
    };
  }, [query, handle]);

  if (!results?.length) return null;

  return (
    <section className="w-full max-w-[1140px] mx-auto px-4 md:px-8 pb-16" aria-label="Similar pieces in stock">
      <div className="flex items-baseline justify-between gap-4 flex-wrap border-t border-border pt-10">
        <h2 className="font-display text-2xl md:text-3xl text-foreground">Similar pieces in stock</h2>
        <Link
          to={`/search?q=${encodeURIComponent(query)}`}
          className="text-xs uppercase tracking-[0.2em] font-semibold underline underline-offset-4 hover:text-primary"
        >
          See all results
        </Link>
      </div>
      <ul className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {results.map((p) => {
          const image = p.images?.edges?.[0]?.node;
          const { price, from: priceIsFrom } = listingPrice(p);
          const name = cleanProductTitle(p.title, p.productType);
          return (
            <li key={p.handle}>
              <Link to={`/product/${p.handle}`} className="group block">
                <div className="aspect-square overflow-hidden bg-muted">
                  {image && (
                    <img
                      src={getShopifyImageUrl(image.url, 400)}
                      alt={image.altText || name}
                      loading="lazy"
                      width={400}
                      height={400}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                </div>
                <p className="mt-2 text-xs md:text-sm text-foreground line-clamp-2">{name}</p>
                {price && <p className="mt-1 text-sm font-semibold text-foreground">{priceIsFrom && "From "}{formatPrice(price.amount, price.currencyCode)}</p>}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
